import React from 'react';
import { Text, View, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import BackButton from '@components/common/backButton';

import { font } from '@styles/fonts';
import FeatureFlags from '@components/featureFlags';
import Svg from '@styles/icons/svg';
import { ROLES, navigationRef } from '@helpers';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { buildSettingsNavParams, isLimitedSettingsStack } from './navigationParams';

const SETTINGS_NAV_TEST_IDS = {
  Account: TEST_IDS.settingsNavigation.accountItem,
  Users: TEST_IDS.settingsNavigation.usersItem,
  Devices: TEST_IDS.settingsNavigation.devicesItem,
  Machines: TEST_IDS.settingsNavigation.machinesItem,
  Notifications: TEST_IDS.settingsNavigation.notificationsItem,
  Connector: TEST_IDS.settingsNavigation.connectorItem,
};

export default ({ C, tabs, selectedTab, navigation, role, rootStack = 'Settings' }) => {
  const isLimited = isLimitedSettingsStack(rootStack);
  return (
    <View style={{ width: '100%', height: '100%' }}>
      <View style={{ paddingHorizontal: 25, marginTop: scale(isLimited ? 30 : 63), marginBottom: scale(17) }}>
        {isLimited && (
          <View style={{ marginBottom: scale(16), marginLeft: scale(-12) }}>
            {/* SettingsLimited is reachable only from /on-hold (useManageOnHold.onSettingsPress).
                Default `navigateBack` would `goBack()` inside the nested SettingsLimited stack
                (Account/Users/…), not exit to /on-hold. Reset the root stack instead so the
                user lands back where they started and browser-back can't re-enter settings. */}
            <BackButton onPress={() => navigationRef.current?.reset({ index: 0, routes: [{ name: 'OnHold' }] })} />
          </View>
        )}
        <Text
          style={[
            {
              color: C('Label / Light / Primary'),
              fontWeight: '700',
            },
            font['Large Title'],
          ]}
        >
          {i18n.t('Settings')}
        </Text>
      </View>
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View style={{ paddingHorizontal: 18 }}>
          {tabs.map((item) => {
            if (item.roles && !item.roles.includes(role)) {
              return null;
            }
            if (isRestrictedForOperator(role, RESTRICTION_CATEGORIES.SETTINGS_ITEMS, item.value)) {
              return null;
            }
            const selected = item.value === selectedTab;
            return (
              <FeatureFlags {...{ name: item.flag, C }} key={item.value + selected}>
                <TouchableElement
                  testID={SETTINGS_NAV_TEST_IDS[item.value]}
                  onPress={() => {
                    navigation.navigate(rootStack, buildSettingsNavParams(rootStack, item.value));
                  }}
                  hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                >
                  {({ pressHoverColor, boxStyle, hovered, pressed }) => (
                    <View
                      key={`${selected}${item.value}`}
                      style={[
                        {
                          paddingHorizontal: scale(7),
                          alignItems: 'center',
                          flexDirection: 'row',
                          height: scale(44),
                          borderRadius: scale(10),
                          backgroundColor: C(
                            pressHoverColor(
                              selected ? 'Selected/Light/List item' : hovered || pressed ? 'Button/Light/Only Text' : 'Background/Light/Detail'
                            )
                          ),
                        },
                        boxStyle,
                      ]}
                    >
                      <Svg
                        icon={`${item.value.replace(/ /g, '')}SettingsIcon`}
                        color={selected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary'}
                        width={scale(32)}
                        height={scale(24)}
                      />
                      <Text
                        style={[
                          font['Body'],
                          { color: C(selected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary'), marginLeft: scale(7) },
                        ]}
                      >
                        {i18n.t(item.value)}
                      </Text>
                    </View>
                  )}
                </TouchableElement>
              </FeatureFlags>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};
